// src/screens/payments/MerchantPaymentScreen.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './MerchantPaymentScreen.css';
import backIcon from '../../assets/backspace-icon.png';

import ChoosePaymentMethodDrawer from '../../components/popups/ChoosePaymentMethodDrawer';

const MerchantPaymentScreen = ({ user }) => {
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // 🎫 Trial state (code redemption result)
  const [trialApplied, setTrialApplied] = useState(false);

  // ✅ Drawer
  const [payMethodOpen, setPayMethodOpen] = useState(false);

  // refs for smooth scroll + accessibility focus
  const errorRef = useRef(null);

  // ✅ Mainnet gate: only allow on barterfi.app (NOT testnet.barterfi.app)
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isTestnetSub = /^testnet\./i.test(host);
  const isBarterfiRoot = /(^|\.)barterfi\.app$/i.test(host);
  const isMainnet = isBarterfiRoot && !isTestnetSub;

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        errorRef.current?.focus?.();
      } catch {}
    }, 0);
  };

  const handleCancel = () => {
    navigate('/marketplace');
  };

  const handleConfirmAndPay = async () => {
    setErrorMsg('');

    // 🔒 Block payments unless on Mainnet
    if (!isMainnet) {
      showError(
        'This merchant payment is only available on Mainnet (barterfi.app). Please open the mainnet URL to proceed.'
      );
      return;
    }

    setLoading(true);
    try {
      const res = await api.patch(
        `/users/mark-merchant-paid/${user.username}`,
        {}, // send empty JSON, not null
        { headers: { 'X-Network': 'mainnet' } }
      );

      if (res.status === 200) {
        console.log('✅ Payment successful! Reloading and redirecting...');
        localStorage.setItem('showMerchantSuccessBanner', 'true');
        setTimeout(() => {
          window.location.href = '/marketplace';
        }, 600);
      } else {
        showError('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);

      const data = err?.response?.data;
      let serverMsg =
        data?.message ||
        data?.error ||
        err?.message ||
        'Payment failed. Please try again.';

      if (data?.error === 'mainnet_only') {
        serverMsg = 'This payment must be completed on Mainnet (barterfi.app).';
      } else if (data?.code === 'WALLET_NOT_FOUND' || /no wallet found/i.test(serverMsg)) {
        serverMsg = 'No wallet found. Please activate your wallet first to complete payment.';
      } else if (data?.code === 'INSUFFICIENT_FUNDS' || /insufficient/i.test(serverMsg)) {
        serverMsg = 'Insufficient balance. Please add funds to your wallet.';
      }

      showError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  // 🎫 Apply code → redeem trial (now accepts a code param)
  const handleApplyCode = async (codeArg) => {
    setErrorMsg('');

    const code = (codeArg || '').trim();

    if (!code) {
      showError('Enter a code to apply.');
      return;
    }
    if (!isMainnet) {
      showError('This free trial can only be redeemed on Mainnet (barterfi.app).');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(
        `/users/redeem-merchant-trial/${user.username}`,
        { code },
        { headers: { 'X-Network': 'mainnet' } }
      );

      if (res.status === 200 && res.data?.success) {
        setTrialApplied(true);
        localStorage.setItem('showMerchantSuccessBanner', 'true');

        setTimeout(() => {
          window.location.href = '/marketplace';
        }, 600);
        return;
      }

      if (res.status === 200 && res.data?.alreadyPaid) {
        setTrialApplied(true);
        showError(res.data?.message || 'Your merchant access is already active.');
        return;
      }

      showError('Could not apply the code. Please try again.');
    } catch (err) {
      const data = err?.response?.data;
      let msg =
        data?.message ||
        data?.error ||
        err?.message ||
        'Could not apply the code. Please try again.';

      if (data?.error === 'mainnet_only') {
        msg = 'This free trial can only be redeemed on Mainnet (barterfi.app).';
      } else if (data?.error === 'code_invalid') {
        msg = 'Invalid code.';
      } else if (data?.error === 'code_already_used') {
        msg = 'This code was already used on your account.';
      }
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const busy = loading;

  // Hide bottom nav while on this screen
  useEffect(() => {
    const nav = document.querySelector('.bottom-nav');
    if (nav) nav.style.display = 'none';
    return () => {
      if (nav) nav.style.display = 'flex';
    };
  }, []);

  // Smooth-scroll to error if it appears
  useEffect(() => {
    if (errorMsg) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        errorRef.current?.focus?.();
      } catch {}
    }
  }, [errorMsg]);

  return (
    <div className="merchantpay-container">
      <div className="merchantpay-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="merchantpay-back-icon"
          onClick={() => navigate(-1)}
        />
        <h3>Merchant Payment</h3>
      </div>

      <div className="merchantpay-body">
        {/* 🌐 Helpful banner if opened on testnet */}
        {!isMainnet && (
          <div className="merchantpay-warning">
            You’re on <strong>Testnet</strong>. Merchant subscription payments require real Pi on
            <strong> Mainnet</strong>. Open <strong>barterfi.app</strong> to continue.
          </div>
        )}

        {/* 🔻 Error (focusable) + quick action */}
        {errorMsg && (
          <>
            <div
              className="merchantpay-error"
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              ref={errorRef}
            >
              {errorMsg}
            </div>

            <div className="merchantpay-cta-row">
              <button className="wallet-btn" onClick={() => navigate('/my-wallet')}>
                Go to My Wallet
              </button>
            </div>
          </>
        )}

        <div className="merchantpay-receipt">
          <h4>🧾 Payment Summary</h4>
          <p><strong>Service:</strong> BarterFi Merchant Access</p>
          <p><strong>Duration:</strong> 30 Days</p>

          <div className="amount-row">
            <strong>Amount:</strong>
            {trialApplied ? (
              <>
                <span className="amount-strike">20 Pi</span>
                <span className="amount-now">0 Pi</span>
                <span className="amount-badge">Trial</span>
              </>
            ) : (
              <span className="amount-now">20 Pi</span>
            )}
          </div>

          <p className="merchantpay-muted">
            You’ll choose your payment method after tapping <strong>Confirm &amp; Pay</strong>.
          </p>
        </div>

        <div className="merchantpay-features">
          <h4>🌟 What's Included</h4>
          <ul>
            <li>✅ Verified Merchant Badge</li>
            <li>✅ Storefront + Product Listings</li>
            <li>✅ Dynamic USD-to-Pi Pricing</li>
            <li>✅ Promote Sponsored Products</li>
            <li>✅ Escrow Payment Protection</li>
            <li>✅ Affordable Monthly Plan</li>
          </ul>

          <p className="read-more-link" onClick={() => navigate('/about-barterfi#merchant')}>
            Read more...
          </p>
        </div>

        <div className="merchantpay-terms">
          <p>
            By tapping confirm, you agree to BarterFi&apos;s monthly merchant terms.
            You can cancel or pause your store anytime, but no refunds are issued once payment is made.
          </p>
        </div>
      </div>

      <div className="merchantpay-footer">
        <button className="cancel-btn" onClick={handleCancel} disabled={busy}>
          Cancel
        </button>

        <button
          className="pay-btn"
          onClick={() => setPayMethodOpen(true)}
          disabled={busy || trialApplied}
          title={
            trialApplied
              ? 'Trial active — no payment needed'
              : (!isMainnet ? 'Open barterfi.app (Mainnet) to pay' : '')
          }
        >
          {busy
            ? 'Processing…'
            : trialApplied
              ? 'Activated'
              : (!isMainnet ? 'Mainnet Only' : 'Confirm & Pay')}
        </button>
      </div>

      <ChoosePaymentMethodDrawer
        open={payMethodOpen}
        onClose={() => setPayMethodOpen(false)}
        isMainnet={isMainnet}
        amountPi={20}
        defaultCode="30dayfree"
        busy={busy || trialApplied}
        onPayWithPi={async () => {
          setPayMethodOpen(false);
          await handleConfirmAndPay();
        }}
        onRedeemCode={async (code) => {
          setPayMethodOpen(false);
          await handleApplyCode(code);
        }}
      />
    </div>
  );
};

export default MerchantPaymentScreen;
